/**
 * Surface Modeling Operations for CAD Viewer
 * These methods extend CADViewer with surface modeling capabilities
 * Include this file after cad-viewer.js
 */

// Extend CADViewer prototype with surface modeling operations
if (typeof CADViewer !== 'undefined') {

  // ===== Surface Modeling Operations =====

  /**
   * Extrude surface from curve
   */
  CADViewer.prototype.surfaceExtrudeOperation = async function() {
    console.log('[CADViewer] Surface extrude operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const curveId = prompt('Enter curve ID to extrude (or leave blank to use selected):');
    if (!curveId && !this.selectedFeatureId) {
      Utils.notify('No curve selected', 'warning');
      return;
    }

    const targetCurveId = curveId || this.selectedFeatureId;
    const distance = parseFloat(prompt('Enter extrusion distance:', '10'));
    if (isNaN(distance)) {
      Utils.notify('Invalid distance', 'warning');
      return;
    }

    try {
      const surfaceId = await this.surfaceModeler.extrudeSurface(targetCurveId, distance);
      this.addFeatureToTree('surface', 'Extruded Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface extrude error:', error);
    }
  };

  /**
   * Revolve surface from curve
   */
  CADViewer.prototype.surfaceRevolveOperation = async function() {
    console.log('[CADViewer] Surface revolve operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const curveId = prompt('Enter curve ID to revolve (or leave blank to use selected):');
    if (!curveId && !this.selectedFeatureId) {
      Utils.notify('No curve selected', 'warning');
      return;
    }

    const targetCurveId = curveId || this.selectedFeatureId;
    const angle = parseFloat(prompt('Enter revolution angle (degrees):', '360'));
    if (isNaN(angle)) {
      Utils.notify('Invalid angle', 'warning');
      return;
    }

    try {
      const surfaceId = await this.surfaceModeler.revolveSurface(targetCurveId, { x: 0, y: 1, z: 0 }, angle);
      this.addFeatureToTree('surface', 'Revolved Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface revolve error:', error);
    }
  };

  /**
   * Loft surface between curves
   */
  CADViewer.prototype.surfaceLoftOperation = async function() {
    console.log('[CADViewer] Surface loft operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const curveIdsStr = prompt('Enter curve IDs separated by commas (e.g., curve1,curve2,curve3):');
    if (!curveIdsStr) {
      Utils.notify('No curves specified', 'warning');
      return;
    }

    const curveIds = curveIdsStr.split(',').map(s => s.trim());
    if (curveIds.length < 2) {
      Utils.notify('At least 2 curves required for lofting', 'warning');
      return;
    }

    try {
      const surfaceId = await this.surfaceModeler.loftSurface(curveIds, 'G1');
      this.addFeatureToTree('surface', 'Lofted Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface loft error:', error);
    }
  };

  /**
   * Sweep surface along path
   */
  CADViewer.prototype.surfaceSweepOperation = async function() {
    console.log('[CADViewer] Surface sweep operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const profileCurveId = prompt('Enter profile curve ID:');
    if (!profileCurveId) {
      Utils.notify('No profile curve specified', 'warning');
      return;
    }

    const pathCurveId = prompt('Enter path curve ID:');
    if (!pathCurveId) {
      Utils.notify('No path curve specified', 'warning');
      return;
    }

    try {
      const surfaceId = await this.surfaceModeler.sweepSurface(profileCurveId, pathCurveId);
      this.addFeatureToTree('surface', 'Swept Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface sweep error:', error);
    }
  };

  /**
   * Offset surface
   */
  CADViewer.prototype.surfaceOffsetOperation = async function() {
    console.log('[CADViewer] Surface offset operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const surfaceId = prompt('Enter surface ID to offset:');
    if (!surfaceId) {
      Utils.notify('No surface specified', 'warning');
      return;
    }

    const distance = parseFloat(prompt('Enter offset distance:', '5'));
    if (isNaN(distance)) {
      Utils.notify('Invalid distance', 'warning');
      return;
    }

    try {
      const newSurfaceId = await this.surfaceModeler.offsetSurface(surfaceId, distance);
      this.addFeatureToTree('surface', 'Offset Surface', newSurfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface offset error:', error);
    }
  };

  /**
   * Ruled surface between two curves
   */
  CADViewer.prototype.surfaceRuledOperation = async function() {
    console.log('[CADViewer] Surface ruled operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const curve1Id = prompt('Enter first curve ID:');
    if (!curve1Id) {
      Utils.notify('No first curve specified', 'warning');
      return;
    }

    const curve2Id = prompt('Enter second curve ID:');
    if (!curve2Id) {
      Utils.notify('No second curve specified', 'warning');
      return;
    }

    try {
      const surfaceId = await this.surfaceModeler.ruledSurface(curve1Id, curve2Id);
      this.addFeatureToTree('surface', 'Ruled Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface ruled error:', error);
    }
  };

  /**
   * Fill closed curve with surface
   */
  CADViewer.prototype.surfaceFillOperation = async function() {
    console.log('[CADViewer] Surface fill operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const curveId = prompt('Enter closed curve ID to fill (or leave blank to use selected):');
    if (!curveId && !this.selectedFeatureId) {
      Utils.notify('No curve selected', 'warning');
      return;
    }

    const targetCurveId = curveId || this.selectedFeatureId;

    try {
      const surfaceId = await this.surfaceModeler.fillSurface(targetCurveId);
      this.addFeatureToTree('surface', 'Filled Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface fill error:', error);
    }
  };

  /**
   * Trim surface with curves
   */
  CADViewer.prototype.surfaceTrimOperation = async function() {
    console.log('[CADViewer] Surface trim operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const surfaceId = prompt('Enter surface ID to trim:');
    if (!surfaceId) {
      Utils.notify('No surface specified', 'warning');
      return;
    }

    const trimmingCurvesStr = prompt('Enter trimming curve IDs separated by commas:');
    if (!trimmingCurvesStr) {
      Utils.notify('No trimming curves specified', 'warning');
      return;
    }

    const trimmingCurveIds = trimmingCurvesStr.split(',').map(s => s.trim());
    const keepInside = confirm('Keep inside portion? (Cancel for outside)');

    try {
      await this.surfaceModeler.trimSurface(surfaceId, trimmingCurveIds, keepInside);
    } catch (error) {
      console.error('[CADViewer] Surface trim error:', error);
    }
  };

  /**
   * Extend surface
   */
  CADViewer.prototype.surfaceExtendOperation = async function() {
    console.log('[CADViewer] Surface extend operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const surfaceId = prompt('Enter surface ID to extend:');
    if (!surfaceId) {
      Utils.notify('No surface specified', 'warning');
      return;
    }

    const edge = prompt('Enter edge (u_min, u_max, v_min, v_max):', 'u_max');
    const distance = parseFloat(prompt('Enter extension distance:', '5'));
    if (isNaN(distance)) {
      Utils.notify('Invalid distance', 'warning');
      return;
    }

    try {
      await this.surfaceModeler.extendSurface(surfaceId, edge, distance, 'natural');
    } catch (error) {
      console.error('[CADViewer] Surface extend error:', error);
    }
  };

  /**
   * Blend surface between two surfaces
   */
  CADViewer.prototype.surfaceBlendOperation = async function() {
    console.log('[CADViewer] Surface blend operation');

    if (!this.surfaceModeler) {
      Utils.notify('Surface modeler not available', 'error');
      return;
    }

    const surface1Id = prompt('Enter first surface ID:');
    if (!surface1Id) {
      Utils.notify('No first surface specified', 'warning');
      return;
    }

    const surface2Id = prompt('Enter second surface ID:');
    if (!surface2Id) {
      Utils.notify('No second surface specified', 'warning');
      return;
    }

    const continuity = confirm('Use G2 continuity? (Cancel for G1)') ? 'G2' : 'G1';

    try {
      const surfaceId = await this.surfaceModeler.blendSurface(surface1Id, surface2Id, continuity);
      this.addFeatureToTree('surface', 'Blend Surface', surfaceId);
    } catch (error) {
      console.error('[CADViewer] Surface blend error:', error);
    }
  };

  console.log('[CADViewer] Surface modeling operations loaded');
}
